import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Copy, Share2, Gift, Sparkles } from "lucide-react";
import { playSfx } from "@/lib/sfx";

/**
 * Referrals — share your code, earn chips when your friends sign up and play.
 * Both parties get a bonus: referrer gets 250 chips per signup, new user gets +100 chips on top of the welcome bonus.
 */
export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const shareLink = data?.code
    ? `${window.location.origin}/auth?ref=${data.code}`
    : "";

  const copyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      playSfx("cashOut");
      toast.success("Invite link copied");
    } catch { toast.error("Copy failed"); }
  };

  const copyCode = async () => {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      playSfx("click");
      toast.success("Code copied");
    } catch { toast.error("Copy failed"); }
  };

  const shareNative = async () => {
    if (!shareLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on VEXIS",
          text: `Ride real skill games at VEXIS. Sign up with my code ${data.code} and we both get bonus chips.`,
          url: shareLink,
        });
        playSfx("cashOut");
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Referrals
          </div>
          <h1 className="font-display text-3xl mt-2">Invite friends · earn chips together</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-xl">
            Share your code with a friend. When they sign up you get <span className="text-[color:var(--gold)] font-mono">+{data?.referrer_bonus || 250}</span> chips and they get <span className="text-[color:var(--gold)] font-mono">+{data?.referee_bonus || 100}</span> chips on top of their welcome bonus.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_320px] gap-6">
        <div className="vexis-card p-6 relative overflow-hidden">
          {/* magical background flourish */}
          <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full pointer-events-none" style={{
            background: "radial-gradient(circle, rgba(244,165,192,0.2) 0%, transparent 70%)",
            filter: "blur(20px)",
          }} />
          <div className="relative">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Your invite code</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="font-display text-4xl sm:text-5xl text-[color:var(--gold)] tracking-widest" data-testid="referral-code">
                {data?.code || "…"}
              </div>
              <button onClick={copyCode} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="copy-code" disabled={!data?.code}>
                <Copy className="w-4 h-4" /> Copy code
              </button>
            </div>
            {shareLink && (
              <>
                <div className="mt-6 text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Share this link</div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-mono text-xs sm:text-sm text-slate-300 flex-1 min-w-[220px] break-all bg-black/30 border border-white/10 rounded-lg px-3 py-2" data-testid="share-link">
                    {shareLink}
                  </div>
                  <button onClick={copyLink} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="copy-link">
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                  <button onClick={shareNative} className="btn-gold text-sm inline-flex items-center gap-2" data-testid="share-link-btn">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
              </>
            )}
            <div className="mt-6 grid grid-cols-3 gap-3">
              <StatCard label="Friends invited" value={data?.total_invited ?? 0} testid="referrals-count" />
              <StatCard label="Chips earned" value={(data?.total_earned_chips ?? 0).toLocaleString()} testid="referrals-earned" gold />
              <StatCard label="Your reward" value={`+${data?.referrer_bonus ?? 250}`} testid="referrals-bonus" />
            </div>
          </div>
        </div>

        <div className="vexis-card p-6 space-y-4">
          <div className="pill inline-flex items-center gap-1"><Gift className="w-3 h-3" /> How it works</div>
          <ol className="space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-3">
              <span className="pill pill-gold shrink-0 mt-0.5">1</span>
              <span>Share your invite link with a friend</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="pill pill-gold shrink-0 mt-0.5">2</span>
              <span>They sign up (code is applied automatically)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="pill pill-gold shrink-0 mt-0.5">3</span>
              <span>You both get bonus chips instantly</span>
            </li>
          </ol>
          <div className="pt-3 mt-3 border-t border-white/5 text-xs text-slate-500 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[color:var(--gold)]" /> No limit — invite as many friends as you like.
          </div>
        </div>
      </div>

      {data?.invited?.length > 0 && (
        <div className="vexis-card p-6">
          <div className="pill mb-3">Friends you invited</div>
          <ul className="divide-y divide-white/5" data-testid="invited-list">
            {data.invited.map((u) => (
              <li key={u._id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-slate-200 font-mono">{u.email_masked}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Joined {new Date(u.created_at).toLocaleDateString()} · {u.stats?.games_played || 0} plays</div>
                </div>
                <div className="text-sm font-mono text-emerald-400">+{data.referrer_bonus} chips earned</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, gold, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-xl ${gold ? "text-[color:var(--gold)]" : ""}`}>{value}</div>
    </div>
  );
}
